/**
 * LiveTrackingPage — the bench view. 2026-09-19 (per Gilles, Phase 7A + 7B v1).
 *
 * Two jobs on one screen, because one person on a bench is doing both:
 *   1. RECORD — narrate the game; audio uploads in ~20s segments for the
 *      transcription broker to turn into stats.
 *   2. TAP — record stats by hand as they happen. This works with the
 *      recorder off, and it is the fallback whenever the voice pipeline is
 *      not trusted yet.
 *
 * v1 is FOREGROUND ONLY: the webview's recorder stops if the phone locks or
 * the tracker switches apps. Rather than hide that, the page says so plainly
 * and the session counts its gaps, so nobody trusts a transcript with holes
 * in it. The native plugin (7B v2) removes the limitation without changing
 * this page's shape.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Mic, Square, Pause, Play, Undo2, AlertTriangle, Radio, Wifi, WifiOff, ChevronDown, ChevronUp, Plus, Minus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api.js';
import useChunkedRecorder from '../hooks/useChunkedRecorder.js';
import { chatSocket } from '../services/socket.js';
import GoalTimeline, { isGoalEvent } from '../components/stats/GoalTimeline.jsx';
import PendingAnnouncements from '../components/PendingAnnouncements.jsx';

const fmtClock = (ms) => {
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

// 2026-10-01 — the mic meter paints itself from the recorder's levelRef on
// its own animation frame, straight into the DOM. No React state, so the
// page does not re-render 60×/s while recording (that re-render storm is
// what froze the tracker and ate taps mid-game).
function MicLevel({ levelRef, active }) {
  const barRef = useRef(null);
  useEffect(() => {
    if (!active) { if (barRef.current) barRef.current.style.width = '0%'; return undefined; }
    let raf = 0, last = -1;
    const tick = () => {
      const lv = levelRef.current || 0;
      const w = Math.round(lv * 20) * 5;            // 5 % steps: fewer style writes
      if (w !== last && barRef.current) {
        last = w;
        barRef.current.style.width = `${w}%`;
        barRef.current.style.background = lv > 0.08 ? 'var(--goal, #00e87a)' : 'var(--text-3)';
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [levelRef, active]);
  return (
    <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden', marginTop: 6 }}>
      <div ref={barRef} style={{ height: '100%', width: '0%', background: 'var(--text-3)', transition: 'width 90ms linear' }} />
    </div>
  );
}

// One side of the score. Module-level on purpose: it was declared inside the
// page's render, which makes React treat it as a brand-new component type
// every render — unmount + remount of the number and both buttons each time.
function ScoreSide({ k, label, n, busy, onNudge }) {
  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div className="text-xs font-bold" style={{ color: 'var(--text-3)', letterSpacing: '0.12em' }}>{label}</div>
      <div style={{ fontSize: 56, fontWeight: 800, lineHeight: 1.05, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>{n}</div>
      <div className="flex justify-center gap-2 mt-1">
        <button type="button" onClick={() => onNudge(k, -1)} disabled={busy || n === 0} aria-label={`${label} minus one`}
          className="flex items-center justify-center rounded-full"
          style={{ width: 40, height: 40, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-2)', cursor: 'pointer', opacity: n === 0 ? 0.4 : 1 }}>
          <Minus size={18} />
        </button>
        <button type="button" onClick={() => onNudge(k, 1)} disabled={busy} aria-label={`${label} plus one`}
          className="flex items-center justify-center rounded-full"
          style={{ width: 40, height: 40, border: 'none', background: 'var(--ice)', color: '#001018', cursor: 'pointer' }}>
          <Plus size={18} />
        </button>
      </div>
    </div>
  );
}

// 2026-10-07 — mini-game clock: how long the current mini-game has been
// going (ticks every second, its own state so the page doesn't re-render),
// plus how long each finished one lasted. Starts come from the server.
const fmtDur = (ms) => {
  if (ms == null) return '—';
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}` : `${m}:${String(r).padStart(2, '0')}`;
};
function MiniGameClock({ clock, currentGameId }) {
  const cur = (clock || []).find(c => c.gameId === currentGameId);
  const [now, setNow] = useState(() => Date.now());
  const running = !!(cur?.startedAt && !cur?.endedAt);
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);
  if (!cur) return null;
  const elapsed = cur.endedAt ? cur.durationMs : (cur.startedAt ? now - new Date(cur.startedAt).getTime() : null);
  const done = (clock || []).filter(c => c.durationMs != null && c.gameId !== currentGameId);
  return (
    <div className="text-center mb-2" data-mini-clock>
      <div className="text-xs font-bold" style={{ color: 'var(--text-3)', letterSpacing: '0.12em' }}>
        ⏱ MINI-GAME {cur.n} {running ? 'TIME' : (cur.endedAt ? 'LASTED' : '')}
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)', color: running ? 'var(--ice)' : 'var(--text-2)' }}>
        {cur.startedAt ? fmtDur(elapsed) : 'starts with the first recording or goal'}
      </div>
      {done.length > 0 && (
        <div className="text-xs" style={{ color: 'var(--text-3)' }}>
          {done.map(c => `#${c.n} ${fmtDur(c.durationMs)}`).join(' · ')}
        </div>
      )}
    </div>
  );
}

export default function LiveTrackingPage() {
  const { teamId, gameId: routeGameId } = useParams();
  // 2026-09-23 — voice mini-games. The recording stays on the session root;
  // "new mini game" spawns a sibling and everything on this page (score,
  // taps, undo) follows it. The recorder is never remounted.
  const [activeGameId, setActiveGameId] = useState(routeGameId);
  const [miniNumber, setMiniNumber] = useState(1);
  useEffect(() => { setActiveGameId(routeGameId); setMiniNumber(1); }, [routeGameId]);
  const gameId = activeGameId;
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [side, setSide]     = useState('home');
  const [busy, setBusy]     = useState(false);
  const [recent, setRecent] = useState([]);       // local undo stack
  const recordingIdRef      = useRef(null);
  // 2026-09-22 (Phase 7C) — what the engine is doing with this recording.
  const [tx, setTx] = useState(null);        // { status:'live'|'off'|'error', shareUrl, playersWithoutNumber }
  const [liveScore, setLiveScore] = useState(null);
  const [liveAsrDown, setLiveAsrDown] = useState(false);   // 2026-10-21 — live STT down on the server
  // 2026-09-23 — during the game the coach wants the score, not 20 rows of
  // buttons. Player grid collapsed by default; manual +/- on the score.
  const [showPlayers, setShowPlayers] = useState(false);
  // 2026-10-01 — WhatsApp game updates (Game Day Stats): how many parents hear each goal.
  const [wa, setWa] = useState(null);
  useEffect(() => { let c = false; api.get(`/teams/${teamId}/whatsapp/summary`).then(({ data: d }) => { if (!c) setWa(d); }).catch(() => {}); return () => { c = true; }; }, [teamId]);
  const [scoreBusy, setScoreBusy] = useState(false);
  const nudgeScore = async (team_side, delta) => {
    if (scoreBusy) return;
    setScoreBusy(true);
    try {
      await api.post(`/teams/${teamId}/game-events/${gameId}/score`, { team_side, delta });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not change the score');
    } finally { setScoreBusy(false); }
  };

  const load = useCallback(async () => {
    let flipped = false;
    try {
      const { data: d } = await api.get(`/teams/${teamId}/game-events/${gameId}`);
      // 2026-10-01 — the URL is always the session root; after a reload land
      // on the latest mini-game (the one being played) and label it right.
      if (d?.session?.latestGameId && String(d.session.latestGameId) !== String(gameId) && String(gameId) === String(routeGameId)) {
        setActiveGameId(String(d.session.latestGameId));
        setMiniNumber(Number(d.session.count) || 1);
        flipped = true;
        return;                                   // the gameId change re-runs load()
      }
      if (d?.session?.number) setMiniNumber(Number(d.session.number));
      setData(d);
    } catch {
      toast.error('Could not load stats for this game');
    } finally {
      if (!flipped) setLoad(false);
    }
  }, [teamId, gameId, routeGameId]);

  useEffect(() => { load(); }, [load]);

  // Socket updates — declared AFTER `load` (const TDZ: referencing it in
  // the deps array above its declaration crashed the page, v2.5.288).
  useEffect(() => {
    // 2026-09-23 — the socket only receives team broadcasts after JOIN_TEAM,
    // which until now only the chat page ever sent. Without it every live
    // goal was broadcast into an empty room and the page needed a refresh.
    chatSocket.joinTeam(teamId);
    const mine = (d) => d && (String(d.gameId) === String(gameId) || String(d.rootGameId) === String(routeGameId));
    const u1 = chatSocket.on('GAME_STATS_GOAL', (d) => {
      if (!mine(d)) return;
      if (!d.manual) { setLiveScore({ score: d.score, period: d.period }); toast.success(`🚨 GOAL${d.event?.name ? ' — ' + d.event.name : ''}`, { duration: 6000 }); }
      load();
    });
    const u2 = chatSocket.on('GAME_STATS_BOX',  (d) => { if (!mine(d)) return; if (d.score) setLiveScore({ score: d.score, period: d.period }); load(); });
    const u3 = chatSocket.on('GAME_MINI_NEW', (d) => {
      if (!d || String(d.fromGameId) !== String(gameId)) return;
      setActiveGameId(String(d.newGameId));
      setMiniNumber(Number(d.number) || 1);
      setLiveScore(null);
      toast.success(`Mini-game ${d.number} started — previous one saved`, { duration: 6000 });
    });
    // 2026-10-21 (Gilles's live test) — the engine's live speech-to-text was
    // down the whole game and the phone showed a green "synced". The server
    // now says so the moment the first chunk bounces; show it loud, once.
    const u4 = chatSocket.on('LIVE_ASR_DOWN', (d) => {
      if (!mine(d)) return;
      setLiveAsrDown(true);
      toast.error(d.message || 'Live transcription is down — audio is saved; the full analysis runs when you stop.', { duration: 12000 });
    });
    return () => { u1?.(); u2?.(); u3?.(); u4?.(); };
  }, [teamId, gameId, load]);

  // ── recorder ───────────────────────────────────────────────────────────
  const uploadChunk = useCallback(async (blob, meta) => {
    const rid = recordingIdRef.current;
    if (!rid) return; // session never opened (server refused) — drop, don't queue forever
    await api.post(
      `/teams/${teamId}/recordings/${rid}/chunk`
        + `?seq=${meta.seq}&durationMs=${meta.durationMs}&offsetMs=${meta.offsetMs}`,
      blob,
      { headers: { 'Content-Type': meta.mime }, timeout: 60000 }
    );
  }, [teamId]);

  const handleGap = useCallback((reason) => {
    if (reason === 'backgrounded') {
      toast('Recording interrupted — keep this screen open', { icon: '⚠️' });
    } else if (reason === 'mic-lost') {
      toast.error('The microphone was taken away — tap the mic to start recording again', { duration: 8000 });
    }
  }, []);

  const rec = useChunkedRecorder({ rotateMs: 20000, onChunk: uploadChunk, onGap: handleGap });
  // 2026-09-30 — a paused recorder sends no audio, and a session with no
  // audio for 10 minutes is closed as abandoned. Ping while paused so an
  // intermission keeps the session (and the mini-game button) alive.
  useEffect(() => {
    if (!rec.isPaused) return undefined;
    const ping = () => { const rid = recordingIdRef.current; if (rid) api.post(`/teams/${teamId}/recordings/${rid}/ping`).catch(() => {}); };
    ping();
    const id = setInterval(ping, 45000);
    return () => clearInterval(id);
  }, [rec.isPaused, teamId]);
  // Belt and braces: the socket can drop on a rink's Wi-Fi. While recording,
  // re-pull the score every 20 s so the number is never more than a chunk old.
  useEffect(() => {
    if (!(rec.isRecording || rec.isPaused)) return undefined;
    const id = setInterval(() => { load(); }, 20000);
    return () => clearInterval(id);
  }, [rec.isRecording, rec.isPaused, load]);

  // 2026-09-23 — mic FIRST, server session second. A denied mic prompt
  // used to leave a 'recording' row nobody could stop (then "Someone is
  // already recording" for everyone). If the server refuses, release the mic.
  const startRecording = async () => {
    setBusy(true);
    // 2026-09-30 — start() says whether the mic is live. (`rec.state` here is
    // the value from the render before the tap, so it could not be used.)
    let micOk = false;
    try { micOk = await rec.start(); } catch { micOk = false; }
    if (!micOk) { setBusy(false); return; }
    try {
      const { data: r } = await api.post(`/teams/${teamId}/recordings/${routeGameId}/start`, {}, { timeout: 40000 });
      recordingIdRef.current = r.recordingId;
      setTx({ status: r.transcription || 'off', shareUrl: r.engineShareUrl || null, playersWithoutNumber: r.playersWithoutNumber,
              mode: r.mode || null, sides: r.sides || null, duplicateFirstNames: r.duplicateFirstNames || [],
              role: r.role || 'primary', engineError: r.engineError || null });
      if (r.role === 'backup') toast('Recording as backup — another recorder is feeding the live stats', { icon: '🎙', duration: 6000 });
      else toast.success(r.transcription === 'live' ? 'Recording — narrate the game, stats will appear live' : 'Recording — narrate the game');
    } catch (e) {
      await rec.stop().catch(() => {});
      if (e.response?.status === 409) {
        toast.error(e.response.data?.message || 'Two people are already recording this game', { duration: 6000 });
      } else if (e.response?.status === 403) {
        toast.error(e.response.data?.message || 'Only the coach or a designated recorder can record', { duration: 6000 });
      } else {
        const why = e.response?.data?.message || e.response?.data?.error || e.message || '';
        toast.error(`Could not start recording${e.response?.status ? ' (' + e.response.status + ')' : ''}${why ? ': ' + why : ''}`, { duration: 8000 });
      }
    } finally { setBusy(false); }
  };

  const stopRecording = async () => {
    setBusy(true);
    const rid = recordingIdRef.current;
    try { await rec.stop(); } catch { /* mic already gone — still close the session */ }
    try {
      if (rid) await api.post(`/teams/${teamId}/recordings/${rid}/stop`, { gapCount: rec.gapCount }, { timeout: 20000 });
      recordingIdRef.current = null;
      toast.success('Recording saved — official stats will follow in a few minutes');
    } catch {
      toast.error('Mic is off, but the server session did not close — tap Stop again or it closes itself in 2 minutes');
    } finally { setBusy(false); }
  };

  // ── tapping ────────────────────────────────────────────────────────────
  const tapStats = useMemo(() => {
    if (!data) return [];
    const tracked = new Set(data.tracked || []);
    return (data.catalogue || []).filter(s => tracked.has(s.code) && s.scope === 'skater');
  }, [data]);

  const tap = async (player, stat) => {
    try {
      const { data: r } = await api.post(`/teams/${teamId}/game-events/${gameId}`, {
        events: [{
          event_type: stat.code,
          user_id: player.userId,
          jersey_number: player.jerseyNumber,
          team_side: side,
          value: 1,
          source: 'manual',
        }],
      });
      const id = r.events?.[0]?.id;
      setRecent(p => [{ id, label: `${stat.short} · ${player.name || '#' + player.jerseyNumber}` }, ...p].slice(0, 12));
      setData(prev => {
        if (!prev) return prev;
        const players = prev.players.map(p => p.userId === player.userId
          ? { ...p, stats: { ...p.stats, [stat.code]: (p.stats[stat.code] || 0) + 1 } } : p);
        return { ...prev, players };
      });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not record that');
    }
  };

  const undo = async (item) => {
    if (!item?.id) return;
    try {
      await api.delete(`/teams/${teamId}/game-events/${gameId}/${item.id}`);
      setRecent(p => p.filter(x => x.id !== item.id));
      load();
    } catch { toast.error('Could not undo'); }
  };

  if (loading) return <div className="p-6 text-center text-secondary">Loading…</div>;
  if (!data)   return <div className="p-6 text-center text-secondary">Game not found.</div>;

  const players = data.players || [];

  return (
    <div className="p-4 max-w-4xl mx-auto" style={{ paddingBottom: 96 }}>
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-xl font-bold">Live Tracking</h1>
        <Link to={`/game/${gameId}`} className="text-sm" style={{ color: 'var(--ice)' }}>Game page →</Link>
      </div>

      {/* ── Recorder ───────────────────────────────────────────────── */}
      <div className="rounded-xl mb-4" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', padding: 14 }}>
        {!rec.supported ? (
          <div className="text-sm" style={{ color: 'var(--text-2)' }}>
            This device can't record audio in the app. You can still tap stats below.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              {(rec.isRecording || rec.isPaused) ? (
                <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
                  {/* Pause / resume — 2026-09-23 */}
                  <button
                    type="button"
                    onClick={rec.isPaused ? rec.resume : rec.pause}
                    disabled={rec.state === 'stopping'}
                    className="flex items-center justify-center rounded-full"
                    style={{
                      width: 60, height: 60, border: 'none', cursor: 'pointer',
                      background: rec.isPaused ? 'var(--goal, #00e87a)' : 'var(--ice)',
                      color: '#001018',
                    }}
                    aria-label={rec.isPaused ? 'Resume recording' : 'Pause recording'}>
                    {rec.isPaused ? <Play size={24} fill="currentColor" /> : <Pause size={24} fill="currentColor" />}
                  </button>
                  <button
                    type="button"
                    onClick={stopRecording}
                    disabled={rec.state === 'stopping'}
                    className="flex items-center justify-center rounded-full"
                    style={{
                      width: 48, height: 48, border: 'none', cursor: 'pointer',
                      background: 'var(--puck, #ff3b3b)', color: '#fff',
                    }}
                    aria-label="Stop recording">
                    <Square size={18} fill="currentColor" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={startRecording}
                  disabled={busy || rec.state === 'starting' || rec.state === 'stopping'}
                  className="flex items-center justify-center rounded-full"
                  style={{
                    width: 60, height: 60, flexShrink: 0, border: 'none', cursor: 'pointer',
                    background: 'var(--ice)', color: '#001018',
                  }}
                  aria-label="Start recording">
                  <Mic size={26} />
                </button>
              )}

              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmtClock(rec.elapsedMs)}
                  </span>
                  {rec.isRecording && (
                    <span className="flex items-center gap-1 text-xs font-bold" style={{ color: 'var(--puck, #ff3b3b)' }}>
                      <Radio size={12} /> REC{tx?.role === 'backup' ? ' · BACKUP' : ''}
                    </span>
                  )}
                  {rec.isPaused && (
                    <span className="flex items-center gap-1 text-xs font-bold" style={{ color: 'var(--text-2)' }}>
                      <Pause size={12} /> PAUSED
                    </span>
                  )}
                </div>
                {/* level meter — proof the mic is actually hearing them */}
                <MicLevel levelRef={rec.levelRef} active={rec.isRecording} />
                <div className="flex items-center gap-3 text-xs mt-1.5" style={{ color: 'var(--text-3)' }}>
                  <span>{rec.chunksSent} uploaded</span>
                  {rec.pending > 0 && (
                    <span className="flex items-center gap-1" style={{ color: 'var(--puck)' }}>
                      <WifiOff size={11} /> {rec.pending} queued
                    </span>
                  )}
                  {rec.pending === 0 && rec.isRecording && (
                    <span className="flex items-center gap-1" style={{ color: 'var(--goal)' }}><Wifi size={11} /> synced</span>
                  )}
                  {rec.gapCount > 0 && <span style={{ color: 'var(--puck)' }}>{rec.gapCount} gap{rec.gapCount === 1 ? '' : 's'}</span>}
                </div>
              </div>
            </div>

            {rec.error && (
              <div className="text-xs mt-2" style={{ color: 'var(--puck)' }}>{rec.error}</div>
            )}
            {/* 2026-09-22 — what the engine is doing. */}
            {tx && (
              <div className="flex items-center gap-2 flex-wrap text-xs mt-2" style={{ color: 'var(--text-2)' }}>
                {liveAsrDown && (
                  <span style={{ color: 'var(--penalty)', display: 'block', fontWeight: 700 }} data-live-asr-down>
                    ⚠ Live transcription is DOWN on the server — keep recording: every second of audio is saved,
                    and the full analysis runs the moment you press Stop. Live scores won&rsquo;t move until then.
                  </span>
                )}
                {!liveAsrDown && tx.status === 'live' && tx.mode !== 'pickup' && <span style={{ color: 'var(--goal)' }}>🎙 Transcribing live — say jersey numbers: "goal by number 44"</span>}
                {!liveAsrDown && tx.status === 'live' && tx.mode === 'pickup' && <span style={{ color: 'var(--goal)' }}>🎙 Transcribing live — "goal Hinkewich, assist Tsandelis", or just the colour: "{tx.sides?.home?.color || 'black'} scores". Say "new mini game" to save this one and start the next. Anything unclear lands in the game recap.</span>}
                {tx.duplicateFirstNames?.length > 0 && (
                  <span style={{ color: 'var(--puck)' }}>⚠ Two players named {tx.duplicateFirstNames.map(n => n[0].toUpperCase() + n.slice(1)).join(', ')} — say last names for them.</span>
                )}
                {tx.status === 'off' && <span style={{ color: 'var(--text-3)' }}>Transcription is off — audio is saved, stats can be entered by hand.</span>}
                {tx.status === 'error' && <span style={{ color: 'var(--puck)' }}>Transcription didn't start — audio is still being saved.{tx.engineError ? ` (${tx.engineError})` : ''}</span>}
                {tx.status === 'backup' && <span>Backup recording — audio is saved; the other recorder's narration drives the live stats.</span>}
                {tx.mode !== 'pickup' && tx.playersWithoutNumber > 0 && (
                  <span style={{ color: 'var(--puck)' }}>⚠ {tx.playersWithoutNumber} player{tx.playersWithoutNumber === 1 ? '' : 's'} with no jersey number — say their name instead.</span>
                )}
                {liveScore?.score && tx.mode !== 'pickup' && (
                  <span className="font-bold" style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                    {liveScore.score.us} – {liveScore.score.them}{liveScore.period != null ? ` · P${liveScore.period}` : ''}
                  </span>
                )}
              </div>
            )}

            {tx?.mode === 'pickup' && tx.sides && (
              <div className="grid gap-2 mt-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
                {['home', 'away'].map(k => (
                  <div key={k} className="rounded-lg p-2" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="text-xs font-bold mb-1" style={{ textTransform: 'capitalize' }}>
                      {k === 'home' ? '🏠 Home' : '🚌 Away'} <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>· {tx.sides[k].color}</span>
                    </div>
                    <div className="text-xs" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
                      {tx.sides[k].players.length ? tx.sides[k].players.join(', ') : '—'}
                    </div>
                  </div>
                ))}
                {tx.sides.unassigned?.length > 0 && (
                  <div className="text-xs" style={{ gridColumn: '1 / -1', color: 'var(--puck)' }}>
                    Not on a side yet (their stats will have no side): {tx.sides.unassigned.join(', ')}
                  </div>
                )}
              </div>
            )}
            {rec.isRecording && (
              <div className="flex items-start gap-2 rounded-lg mt-3 p-2.5"
                style={{ background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.3)' }}>
                <AlertTriangle size={14} style={{ color: 'var(--puck)', flexShrink: 0, marginTop: 1 }} />
                <div className="text-xs" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
                  Keep this screen open. Locking the phone or switching apps stops the recording
                  until you come back — any break is counted and flagged on the transcript.
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 2026-10-17 — WhatsApp updates held for the keeper ("check before
          sending"), right under the recorder where the keeper is looking. */}
      {wa?.enabled && wa.confirm && wa.canKeep && <PendingAnnouncements teamId={teamId} gameId={gameId} title="Send to parents?" />}

      {/* ── Score (2026-09-23) — goals in the event log, both sides.
          Voice goals land here as they're confirmed; +/- adds an
          unattributed goal (recap asks who) or removes the last one. */}
      {(() => {
        // 2026-10-18 — the server's score (one rule everywhere): AWAY includes
        // the opponent goals the voice engine heard (league mode writes them as
        // goals against) but not the goalie's GA line from the post-game grid.
        const home = data.score?.home ?? (data.team?.home?.stats?.goal || 0);
        const away = data.score?.away ?? (data.team?.away?.stats?.goal || 0);
        return (
          <div className="rounded-xl mb-3 p-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
            {miniNumber > 1 && (
              <div className="text-xs font-bold text-center mb-1" style={{ color: 'var(--ice)', letterSpacing: '0.12em' }}>MINI-GAME {miniNumber}</div>
            )}
            <MiniGameClock clock={data.session?.clock} currentGameId={gameId} />
            <div className="flex items-center">
              <ScoreSide k="home" label="HOME" n={home} busy={scoreBusy} onNudge={nudgeScore} />
              <div style={{ fontSize: 28, color: 'var(--text-3)', padding: '0 6px', alignSelf: 'center' }}>–</div>
              <ScoreSide k="away" label="AWAY" n={away} busy={scoreBusy} onNudge={nudgeScore} />
            </div>
            {/* 2026-10-01 — always here, recording or not. It used to show only
                while this phone was recording, so a tracker keeping score by
                hand (or whose recording had died) could not start the next
                mini-game without reloading. With a live recording the split
                is stamped on the tape via the recording route; otherwise the
                game route does Save & New directly. */}
            {/* 2026-10-07 — Save (stamp the score, end this mini-game, stay here)
                and Save & new (same, then start the next). Both work while
                recording — the recorder keeps going across the split. */}
            <div className="flex gap-2 mt-3">
              <button type="button" disabled={scoreBusy || data.gameStatus === 'completed'}
                onClick={async () => {
                  if (!window.confirm(`Save mini-game ${miniNumber} as ${home}–${away}?`)) return;
                  setScoreBusy(true);
                  try {
                    await api.post(`/teams/${teamId}/game-events/${gameId}/save`, {});
                    toast.success(`Mini-game ${miniNumber} saved ${home}–${away}`);
                    await load();
                  } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
                  finally { setScoreBusy(false); }
                }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                style={{ background: 'transparent', border: '1px solid var(--border)', color: data.gameStatus === 'completed' ? 'var(--text-3)' : 'var(--text-2)', cursor: 'pointer' }}>
                {data.gameStatus === 'completed' ? '✓ Saved' : '💾 Save'}
              </button>
              <button type="button" disabled={scoreBusy}
                onClick={async () => {
                  if (!window.confirm(`Save mini-game ${miniNumber} as ${home}–${away} and start the next one?`)) return;
                  setScoreBusy(true);
                  try {
                    const rid = (rec.isRecording || rec.isPaused) ? recordingIdRef.current : null;
                    const { data: r } = rid
                      ? await api.post(`/teams/${teamId}/recordings/${rid}/mini-game`, { atMs: rec.elapsedMs })
                      : await api.post(`/teams/${teamId}/game-events/${gameId}/mini-game`, { atMs: rec.elapsedMs });
                    if (r?.created === false) toast('Already on the latest mini-game', { icon: 'ℹ️' });
                    // the GAME_MINI_NEW broadcast flips the page; fallback if the socket is down:
                    if (r?.gameId && r.gameId !== gameId) { setActiveGameId(String(r.gameId)); setMiniNumber(Number(r.number) || miniNumber + 1); setLiveScore(null); }
                  } catch (e) { toast.error(e.response?.data?.error || 'Could not start the next mini-game'); }
                  finally { setScoreBusy(false); }
                }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                style={{ background: 'transparent', border: '1px solid var(--ice)', color: 'var(--ice)', cursor: 'pointer' }}>
                ⏭ Save &amp; new
              </button>
            </div>
            {(data.reviewCount || 0) > 0 && (
              <div className="text-xs mt-2 text-center" style={{ color: 'var(--text-3)' }}>
                {data.reviewCount} goal{data.reviewCount === 1 ? '' : 's'} to attribute in the game recap
              </div>
            )}
            {/* 2026-10-07 — every goal as announced: who is tagged, and whether the voice AI picked it up */}
            {(data.events || []).some(isGoalEvent) && (
              <div className="mt-3" data-goal-list>
                <GoalTimeline data={data} opponent={data.opponent} />
              </div>
            )}
            {wa?.enabled && (
              <div className="text-xs mt-2 text-center" style={{ color: 'var(--text-3)' }}>
                📲 {wa.level === 'all' ? 'Play-by-play goes' : 'Goals go'} to {wa.subscribers} parent{wa.subscribers === 1 ? '' : 's'} on WhatsApp{wa.confirm ? ' — after you tap Send' : ''}
              </div>
            )}

          </div>
        );
      })()}

      {/* ── Players (collapsed by default) ───────────────────────── */}
      <button type="button" onClick={() => setShowPlayers(v => !v)}
        className="w-full flex items-center justify-between rounded-xl px-3 py-2.5 mb-3 text-sm font-bold"
        style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer' }}>
        <span>Player stats{players.length ? ` (${players.length})` : ''}</span>
        {showPlayers ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {showPlayers && (<>
      {/* ── Which side ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs" style={{ color: 'var(--text-3)' }}>Recording for</span>
        {['home', 'away'].map(s => (
          <button key={s} type="button" onClick={() => setSide(s)}
            className="px-3 py-1 rounded-lg text-xs font-bold" style={{
              border: `1px solid ${side === s ? 'var(--ice)' : 'var(--border)'}`,
              background: side === s ? 'var(--ice-a8, rgba(0,212,255,0.1))' : 'transparent',
              color: side === s ? 'var(--ice)' : 'var(--text-2)', cursor: 'pointer',
            }}>
            {s === 'home' ? 'Home' : 'Away'}
          </button>
        ))}
      </div>

      {/* ── Undo ───────────────────────────────────────────────────── */}
      {recent.length > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>Just added:</span>
          {recent.slice(0, 4).map(item => (
            <button key={item.id} type="button" onClick={() => undo(item)}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs"
              style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer' }}>
              <Undo2 size={11} /> {item.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Tap grid ───────────────────────────────────────────────── */}
      {tapStats.length === 0 ? (
        <div className="rounded-xl p-4 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>
          No stats are turned on for this team yet. Turn some on in{' '}
          <Link to={`/team/${teamId}/settings`} style={{ color: 'var(--ice)', fontWeight: 700 }}>Team Settings → Stats</Link>.
        </div>
      ) : (
        <div className="rounded-xl" style={{ border: '1px solid var(--border)', overflow: 'hidden' }}>
          {players.map((p, i) => (
            <div key={p.userId} className="p-2.5"
              style={{ borderTop: i === 0 ? 'none' : '1px solid var(--border)', background: i % 2 ? 'var(--surface-2)' : 'transparent' }}>
              <div className="flex items-center gap-2 mb-1.5">
                {p.jerseyNumber != null && (
                  <span className="text-xs font-bold" style={{ color: 'var(--text-3)', minWidth: 22 }}>#{p.jerseyNumber}</span>
                )}
                <span className="text-sm font-semibold" style={{ flex: 1, minWidth: 0 }}>{p.name || 'Unknown player'}</span>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {tapStats.map(s => (
                  <button key={s.code} type="button" onClick={() => tap(p, s)}
                    className="px-2.5 py-1.5 rounded-lg text-xs font-bold"
                    style={{ background: 'var(--surface-3, var(--surface-2))', border: '1px solid var(--border)', cursor: 'pointer', minWidth: 52 }}>
                    {s.short}
                    <span style={{ color: 'var(--ice)', marginLeft: 5, fontVariantNumeric: 'tabular-nums' }}>
                      {p.stats?.[s.code] || 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}
